import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { generateToken } from "app/services/api/management";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PrintIcon from "@mui/icons-material/Print";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import {
  listTables,
  createTable,
  updateTable,
  deleteTable,
  rotateTableToken,
  getTableQrDataUrl,
  openTablesQrSheet,
  listQrCalls,
  markCallDone,
  getQrSettings,
} from "app/services/api/qrOrders";

const emptyForm = { name: "", number: "", zone: "", seats: 0, active: true };

const QrTableMap = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser() || {};
  // QR admin endpoints derive customerId from the JWT — bind to the current (edited) customer's
  // token so the table map never operates on the wrong account, regardless of the ambient token.
  const customerId = authUser?.customer?.id ?? authUser?.customerId ?? authUser?.id;
  const tokenRef = useRef(undefined); // undefined = not tried, null = failed, string = minted token
  const tok = useCallback(async () => {
    if (tokenRef.current !== undefined) return tokenRef.current || undefined;
    if (!customerId) { tokenRef.current = null; return undefined; }
    try { const got = await generateToken(customerId); tokenRef.current = got || null; return got || undefined; }
    catch { tokenRef.current = null; return undefined; }
  }, [customerId]);

  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);

  // table editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // table row or null (new)
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // QR preview dialog
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null); // { dataUrl, url, name }

  // live feed — only when the customer has staff-calling enabled (callStaff.waiter/bill)
  const [calls, setCalls] = useState([]);
  const [callStaffActive, setCallStaffActive] = useState(false);

  const loadTables = useCallback(async () => {
    setLoading(true);
    try {
      setTables(await listTables(await tok()));
    } catch (e) {
      enqueueSnackbar(t("pages.qrTableMap.loadError"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t, tok]);

  // Poll the live feed (open staff calls) every 5s.
  const refreshLive = useCallback(async () => {
    try {
      const a = await tok();
      setCalls(await listQrCalls(a));
    } catch {
      /* ignore transient errors */
    }
  }, [tok]);

  useEffect(() => {
    let alive = true;
    let intervalId = null;
    (async () => {
      await loadTables();
      // The live staff-calls feed is only relevant when the customer enabled calling the staff
      // (Zavolať čašníka / Účet). If neither is on, don't show it and don't poll.
      let active = false;
      try {
        const s = await getQrSettings(await tok());
        const cs = (s && s.callStaff) || {};
        active = cs.waiter === true || cs.bill === true;
      } catch (e) {
        /* ignore — leave the feed hidden on error */
      }
      if (!alive) return;
      setCallStaffActive(active);
      if (active) {
        refreshLive();
        intervalId = setInterval(refreshLive, 5000);
      }
    })();
    return () => {
      alive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [loadTables, refreshLive, tok]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", number: row.number || "", zone: row.zone || "", seats: row.seats || 0, active: row.active !== false });
    setEditorOpen(true);
  };

  const save = async () => {
    if (!form.name || !String(form.name).trim()) {
      enqueueSnackbar(t("pages.qrTableMap.nameRequired"), { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateTable(editing._id, form, await tok());
      } else {
        await createTable(form, await tok());
      }
      setEditorOpen(false);
      await loadTables();
      enqueueSnackbar(t("pages.qrTableMap.saved"), { variant: "success" });
    } catch (e) {
      enqueueSnackbar(t("pages.qrTableMap.saveError"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (row) => {
    showDialog({
      variant: "confirm",
      title: t("pages.qrTableMap.deleteTitle"),
      content: t("pages.qrTableMap.deleteConfirm", { name: row.name }),
      onYes: async () => {
        hideDialog();
        try {
          await deleteTable(row._id, await tok());
          await loadTables();
          enqueueSnackbar(t("pages.qrTableMap.deleted"), { variant: "success" });
        } catch {
          enqueueSnackbar(t("pages.qrTableMap.saveError"), { variant: "error" });
        }
      },
      onNo: hideDialog,
    });
  };

  const rotate = (row) => {
    showDialog({
      variant: "confirm",
      title: t("pages.qrTableMap.rotateTitle"),
      content: t("pages.qrTableMap.rotateConfirm", { name: row.name }),
      onYes: async () => {
        hideDialog();
        try {
          await rotateTableToken(row._id, await tok());
          await loadTables();
          enqueueSnackbar(t("pages.qrTableMap.rotated"), { variant: "success" });
        } catch {
          enqueueSnackbar(t("pages.qrTableMap.saveError"), { variant: "error" });
        }
      },
      onNo: hideDialog,
    });
  };

  const showQr = async (row) => {
    setQrData(null);
    setQrOpen(true);
    try {
      const d = await getTableQrDataUrl(row._id, await tok());
      setQrData({ ...d, name: row.name });
    } catch {
      enqueueSnackbar(t("pages.qrTableMap.qrError"), { variant: "error" });
      setQrOpen(false);
    }
  };

  const doCallDone = async (id) => {
    try {
      await markCallDone(id, undefined, await tok());
      await refreshLive();
    } catch {
      enqueueSnackbar(t("pages.qrTableMap.saveError"), { variant: "error" });
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h1">{t("pages.qrTableMap.title")}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={async () => openTablesQrSheet(undefined, await tok())}>
            {t("pages.qrTableMap.printSheet")}
          </Button>
          <Button variant="contained" startIcon={<AddBoxIcon />} onClick={openNew}>
            {t("pages.qrTableMap.addTable")}
          </Button>
        </Stack>
      </Stack>

      {/* Live: staff calls awaiting handling — only when staff-calling is enabled for the customer */}
      {callStaffActive ? (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <NotificationsActiveIcon color={calls.length ? "warning" : "disabled"} />
            <Typography variant="h3" sx={{ m: 0 }}>
              {t("pages.qrTableMap.liveCalls")}
            </Typography>
            {calls.length > 0 && <Chip size="small" color="warning" label={calls.length} />}
          </Stack>
          {calls.length === 0 ? (
            <Typography color="text.secondary">{t("pages.qrTableMap.noCalls")}</Typography>
          ) : (
            <Stack spacing={1}>
              {calls.map((c) => (
                <Stack
                  key={c._id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
                >
                  <Typography>
                    <b>{c.tableName || c.tableId}</b>
                    {c.reason ? ` — ${c.reason}` : ""}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => doCallDone(c._id)}>
                    {t("pages.qrTableMap.callDone")}
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      ) : null}

      {/* Tables list */}
      <Card>
        <CardContent>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : tables.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t("pages.qrTableMap.noTables")}
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("pages.qrTableMap.colName")}</TableCell>
                    <TableCell>{t("pages.qrTableMap.colZone")}</TableCell>
                    <TableCell align="right">{t("pages.qrTableMap.colSeats")}</TableCell>
                    <TableCell>{t("pages.qrTableMap.colActive")}</TableCell>
                    <TableCell align="right">{t("pages.qrTableMap.colActions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tables.map((row) => (
                    <TableRow key={row._id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.zone || "—"}</TableCell>
                      <TableCell align="right">{row.seats || 0}</TableCell>
                      <TableCell>
                        {row.active !== false ? (
                          <Chip size="small" color="success" label={t("pages.qrTableMap.active")} />
                        ) : (
                          <Chip size="small" label={t("pages.qrTableMap.inactive")} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip arrow title={t("pages.qrTableMap.showQr")}>
                          <IconButton size="small" onClick={() => showQr(row)}>
                            <QrCode2Icon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip arrow title={t("pages.qrTableMap.rotate")}>
                          <IconButton size="small" onClick={() => rotate(row)}>
                            <AutorenewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip arrow title={t("pages.qrTableMap.edit")}>
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip arrow title={t("pages.qrTableMap.delete")}>
                          <IconButton size="small" color="error" onClick={() => confirmDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? t("pages.qrTableMap.editTitle") : t("pages.qrTableMap.addTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t("pages.qrTableMap.colName")}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label={t("pages.qrTableMap.colNumber", { defaultValue: "Číslo stola (roh samolepky)" })}
              value={form.number}
              onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
              fullWidth
              helperText={t("pages.qrTableMap.colNumberHelp", { defaultValue: "Zobrazí sa v pravom hornom rohu QR samolepky." })}
            />
            <TextField
              label={t("pages.qrTableMap.colZone")}
              value={form.zone}
              onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t("pages.qrTableMap.colSeats")}
              type="number"
              value={form.seats}
              onChange={(e) => setForm((f) => ({ ...f, seats: Number(e.target.value) }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
              }
              label={t("pages.qrTableMap.colActive")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>{t("pages.qrTableMap.cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t("pages.qrTableMap.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR preview dialog */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs">
        <DialogTitle>{qrData?.name ? `QR — ${qrData.name}` : "QR"}</DialogTitle>
        <DialogContent>
          {!qrData ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={1}>
              <img src={qrData.dataUrl} alt="QR" style={{ width: 280, height: 280 }} />
              <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                {qrData.url}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>{t("pages.qrTableMap.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QrTableMap;
